import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Mail, KeyRound, Copy, Trash2, Clock, CheckCircle2, Link2 } from "lucide-react";

const NEW_COMPANY = "__new__";

export default function Users() {
  const [data, setData] = useState({ users: [], pending_invites: [] });
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company_id: NEW_COMPANY, company_name: "", method: "invite_link" });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/users").then((r) => setData(r.data)).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name: "", email: "", company_id: NEW_COMPANY, company_name: "", method: "invite_link" });
    setResult(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name, email: form.email, method: form.method,
      company_id: form.company_id === NEW_COMPANY ? null : form.company_id,
      company_name: form.company_id === NEW_COMPANY ? form.company_name : null,
    };
    try {
      const { data: res } = await api.post("/admin/users", payload);
      setResult(res);
      toast.success(res.method === "temp_password" ? "Login created with a temporary password" : "Invite created");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name}'s login?`)) return;
    await api.delete(`/admin/users/${u.id}`);
    toast.success("Login removed");
    load();
  };

  return (
    <div className="animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-sky-600">Access</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Employer Logins</h1>
          <p className="text-slate-500 mt-1">Create portal accounts for employers. Only you can add new users.</p>
        </div>
        <Button onClick={openNew} data-testid="create-employer-button" className="bg-sky-600 hover:bg-sky-700 rounded-full font-semibold"><UserPlus className="w-4 h-4 mr-1.5" /> Create employer login</Button>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-lg">Active logins</h2></div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold px-6 py-3">Name</th>
              <th className="text-left font-semibold px-6 py-3">Email</th>
              <th className="text-left font-semibold px-6 py-3">Company</th>
              <th className="text-left font-semibold px-6 py-3">Status</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.users.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-400">No employer logins yet.</td></tr>}
            {data.users.map((u) => (
              <tr key={u.id} data-testid={`employer-row-${u.id}`} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 font-semibold text-slate-800">{u.name}</td>
                <td className="px-6 py-4 text-slate-600">{u.email}</td>
                <td className="px-6 py-4 text-slate-600">{u.company_name}</td>
                <td className="px-6 py-4">
                  {u.must_change_password
                    ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold"><Clock className="w-3 h-3" /> Must set password</span>
                    : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-semibold"><CheckCircle2 className="w-3 h-3" /> Active</span>}
                </td>
                <td className="px-6 py-4 text-right"><button onClick={() => removeUser(u)} data-testid={`delete-employer-${u.id}`} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pending_invites.length > 0 && (
        <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100"><h2 className="font-display font-bold text-lg">Pending invites</h2></div>
          <div className="divide-y divide-slate-100">
            {data.pending_invites.map((i) => (
              <div key={i.invite_url} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                <div>
                  <p className="font-semibold text-slate-800">{i.name} <span className="text-slate-400 font-normal">· {i.email}</span></p>
                  <p className="text-xs text-slate-500">{i.company_name}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => copy(i.invite_url, "Invite link")} className="rounded-full border-slate-300"><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{result ? "Login created" : "Create employer login"}</DialogTitle></DialogHeader>

          {!result ? (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Full name</Label><Input data-testid="employer-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Email</Label><Input data-testid="employer-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
              </div>
              <div>
                <Label>Company</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="employer-company-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NEW_COMPANY}>+ New company</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.company_id === NEW_COMPANY && (
                <div><Label>New company name</Label><Input data-testid="employer-new-company-input" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5" /></div>
              )}
              <div>
                <Label className="mb-2 block">How should they get access?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" data-testid="method-invite" onClick={() => setForm({ ...form, method: "invite_link" })}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${form.method === "invite_link" ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <Mail className={`w-5 h-5 mb-1.5 ${form.method === "invite_link" ? "text-sky-600" : "text-slate-400"}`} />
                    <p className="font-semibold text-sm text-slate-800">Email invite link</p>
                    <p className="text-xs text-slate-500">They set their own password</p>
                  </button>
                  <button type="button" data-testid="method-temp" onClick={() => setForm({ ...form, method: "temp_password" })}
                    className={`text-left p-3 rounded-xl border-2 transition-all ${form.method === "temp_password" ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}>
                    <KeyRound className={`w-5 h-5 mb-1.5 ${form.method === "temp_password" ? "text-sky-600" : "text-slate-400"}`} />
                    <p className="font-semibold text-sm text-slate-800">Temporary password</p>
                    <p className="text-xs text-slate-500">You share it; they reset on login</p>
                  </button>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="submit-employer-button" className="bg-sky-600 hover:bg-sky-700">{saving ? "Creating…" : "Create login"}</Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4" data-testid="employer-result">
              <div className="rounded-xl bg-sky-50 border border-sky-100 p-4">
                <p className="text-sm text-slate-700"><strong>{result.name}</strong> · {result.email}</p>
                <p className="text-xs text-slate-500 mt-0.5">{result.company_name}</p>
              </div>
              {result.method === "temp_password" ? (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Temporary password</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <code data-testid="temp-password-value" className="flex-1 font-mono text-lg font-bold bg-slate-900 text-sky-300 rounded-lg px-4 py-3">{result.temp_password}</code>
                    <Button type="button" onClick={() => copy(result.temp_password, "Password")} className="bg-sky-600 hover:bg-sky-700"><Copy className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Share this with the employer securely. They'll be required to set a new password when they first sign in at your portal login page.</p>
                </div>
              ) : (
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Invite link {result.email_sent ? "(also emailed)" : ""}</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div data-testid="invite-link-value" className="flex-1 flex items-center gap-2 text-sm bg-slate-100 rounded-lg px-3 py-3 text-slate-700 truncate"><Link2 className="w-4 h-4 shrink-0 text-slate-400" /><span className="truncate">{result.invite_url}</span></div>
                    <Button type="button" onClick={() => copy(result.invite_url, "Invite link")} className="bg-sky-600 hover:bg-sky-700"><Copy className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">{result.email_sent ? "We emailed this secure link to the employer. " : ""}The link expires in 7 days and lets them set their own password.</p>
                </div>
              )}
              <DialogFooter>
                <Button type="button" onClick={() => setOpen(false)} data-testid="close-result-button" className="bg-slate-900 hover:bg-slate-800">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
